import axios from 'axios';
import type {
  SystemSettingResponse,
  SystemSettingsByCategoryView,
  SystemSettingUpdateRequest,
  SystemSettingCategoryResponse,
  SettingSearchParams,
} from '../types/setting';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const settingApi = {
  /**
   * GET /api/eraf/system-settings
   * Returns settings grouped by category.
   */
  listByCategory: (
    params?: SettingSearchParams
  ): Promise<SystemSettingsByCategoryView[]> => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.keyword) searchParams.set('keyword', params.keyword);
    const qs = searchParams.toString();
    return apiClient
      .get<unknown, { data: SystemSettingsByCategoryView[] }>(
        `/system-settings${qs ? `?${qs}` : ''}`
      )
      .then((res) => res.data);
  },

  /**
   * GET /api/eraf/system-settings/{key}
   * Returns a single setting detail.
   */
  getByKey: (key: string): Promise<SystemSettingResponse> =>
    apiClient
      .get<unknown, { data: SystemSettingResponse }>(`/system-settings/${key}`)
      .then((res) => res.data),

  /**
   * PATCH /api/eraf/system-settings/{key}
   * Updates the value of a setting.
   */
  update: (
    key: string,
    body: SystemSettingUpdateRequest
  ): Promise<SystemSettingResponse> =>
    apiClient
      .patch<unknown, { data: SystemSettingResponse }>(`/system-settings/${key}`, body)
      .then((res) => res.data),

  /**
   * POST /api/eraf/system-settings/{key}/reset
   * Restores the setting to its default value.
   */
  reset: (key: string): Promise<SystemSettingResponse> =>
    apiClient
      .post<unknown, { data: SystemSettingResponse }>(`/system-settings/${key}/reset`)
      .then((res) => res.data),

  /**
   * GET /api/eraf/system-settings/categories
   * Returns the category catalog with labels and icons.
   */
  getCategories: (
    locale = 'ko'
  ): Promise<SystemSettingCategoryResponse[]> => {
    return apiClient
      .get<unknown, { data: SystemSettingCategoryResponse[] }>(
        `/system-settings/categories?locale=${locale}`
      )
      .then((res) => res.data);
  },
};
